import React from "react";
import { TextField } from "@material-ui/core";
import Autocomplete, { RenderInputParams } from "@material-ui/lab/Autocomplete";
import ISelectItem from "../../models/ISelectItem";
import { stringSort } from "../../functions/sorts";
import styled from "styled-components";
import { IFormError } from "./FormErrors";

const Container = styled.div<{ readonly?: boolean }>`
  width: 100%;

  ${props =>
    props.readonly &&
    `
  & .MuiAutocomplete-endAdornment {
    display: none;
  }
  `}
`;

const StyledTextField = styled(TextField)<{ readonly?: boolean }>`
  & textarea {
    line-height: 25px;
  }

  ${props =>
    props.readonly &&
    `
  & .MuiInput-underline.Mui-disabled:before {
    border-bottom: none;
  }

  & .MuiInput-underline:before {
    border-bottom: none;
  }

  & .MuiInputBase-root.Mui-disabled {
    color: rgba(0, 0, 0, 0.87);
    cursor: default;
  }

  & .MuiFormLabel-asterisk {
    display: none;
  }
  `}
`;

export interface IFormSelectProps<TItem extends ISelectItem> {
  name: string;
  label: string;
  option?: TItem | null;
  prompt: string;
  options: TItem[];
  onValueChange: (val: TItem | null) => void;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: IFormError;
}

function FormSelect<TItem extends ISelectItem>(props: IFormSelectProps<TItem>) {
  const { name, label, option, prompt, options, onValueChange, disabled, readonly, required, className, error } = props;

  let labelVal = label;
  if (!option) {
    if (readonly) {
      labelVal = `${label} - None`;
    } else {
      labelVal = prompt;
    }
  }

  return (
    <Container readonly={readonly} className={className}>
      <Autocomplete
        className={className}
        options={options.sort(stringSort(o => o.Name))}
        getOptionLabel={(option: TItem) => option.Name}
        value={option}
        renderInput={(params: RenderInputParams) => (
          <StyledTextField
            name={name}
            {...params}
            label={labelVal}
            variant="outlined"
            fullWidth
            disabled={readonly || disabled}
            readonly={readonly}
            required={required}
            error={error && error.error}
            helperText={error && error.message}
          />
        )}
        onChange={(event: any, value: TItem | null) => onValueChange(value)}
        disabled={readonly || disabled}
      />
    </Container>
  );
}

export default FormSelect;
